import React, { useEffect, useState } from 'react';
import { Form, Input, Tag } from 'antd';
import PropTypes from 'prop-types';
import { ValidationRule } from 'antd/lib/form';
import { FormComponentProps } from 'antd/lib/form';
const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,5}$/i;

interface InvitedUsersEmailsFieldProps extends FormComponentProps {
  memberEmails: string[];
  onEmailTagsChange: (tags: string[]) => void;
  emailTags: string[];
  disabled: boolean;
  hasInvalidEmailCallback: (hasInvalidEmail: boolean) => void;
}

const InvitedUsersEmailsField: React.FC<InvitedUsersEmailsFieldProps> = ({
  form,
  memberEmails,
  onEmailTagsChange,
  emailTags,
  disabled,
  hasInvalidEmailCallback
}) => {
  const [inputValue, setInputValue] = useState('');
  const [inputTouched, setInputTouched] = useState(false);
  const { getFieldDecorator } = form;
  const maxInvitationLimit = 25;

  // useEffect is needed here to manually trigger validation.
  // AntD has this bug that onBlur does not validate as KeyEnter does.
  useEffect(() => {
    if (inputTouched && !disabled) {
      form.validateFields(['emailAddresses'], { force: true });
    }
  }, [emailTags, inputTouched]);

  const validateEmailAddress = (email: string) => {
    if (email.length === 0) {
      return null;
    }
    const isInvalidEmail = !EMAIL_REGEX.test(email);
    return {
      email: email,
      isValid: !isInvalidEmail
    };
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputTouched(true);
    setInputValue(e.target.value);
  };

  const confirmInput = (el: HTMLTextAreaElement) => {
    const emailAddresses = el.value.split(',').map(email => email.trim());
    let validEmails: string[] = [];
    let remainingEmails: string[] = [];

    emailAddresses.forEach(email => {
      if (memberEmails.includes(email)) return;
      if (emailTags.includes(email) || validEmails.includes(email)) return;

      const processedEmail = validateEmailAddress(email);
      if (processedEmail?.isValid) {
        if (validEmails.length < maxInvitationLimit) {
          validEmails.push(processedEmail.email);
        } else {
          remainingEmails.push(email);
        }
      } else {
        if (email) remainingEmails.push(email);
      }
    });

    if (validEmails.length > 0) {
      onEmailTagsChange([...emailTags, ...validEmails]);
    }

    const newValue = remainingEmails.join(',');
    // Note: This is a controlled input. After much experimentation, Ant Design seems to require me to manually update all states.
    el.value = newValue;
    setInputValue(newValue);
    form.setFieldsValue({ emailAddresses: newValue });
  };

  const keyEnterHandler = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    event.preventDefault();
    confirmInput(event.currentTarget);
  };

  const blurHandler = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    confirmInput(event.currentTarget);
  };

  const handleTagClose = (removedTag: string) => {
    const newTags = emailTags.filter(tag => tag !== removedTag);
    onEmailTagsChange(newTags);
  };

  const getTagColor = (email: string) => {
    if (memberEmails.includes(email)) {
      return 'orange';
    } else if (!EMAIL_REGEX.test(email)) {
      return 'red';
    } else if (emailTags.indexOf(email) !== emailTags.lastIndexOf(email)) {
      return 'orange';
    } else if (emailTags.indexOf(email) >= maxInvitationLimit) {
      return 'red';
    }
    return '#1890ff';
  };

  const emailValidator = (
    _rule: ValidationRule,
    value: string,
    callback: (message?: unknown) => void
  ) => {
    const allEmails = [
      ...emailTags,
      ...(value ? value.split(',').map((email: string) => email.trim()) : [])
    ].filter(email => email.length > 0);

    if (allEmails.length === 0) {
      hasInvalidEmailCallback(true);
      callback('Please enter at least one email address');
      return;
    }

    const invalidEmails = allEmails.filter((email: string) => !EMAIL_REGEX.test(email));
    const alreadyMembers = allEmails.filter(email => memberEmails.includes(email));
    const duplicatedEmails = allEmails.filter((email, index) => allEmails.indexOf(email) !== index);
    const exceededMaxEmails = allEmails.slice(maxInvitationLimit);

    if (
      invalidEmails.length > 0 ||
      alreadyMembers.length > 0 ||
      duplicatedEmails.length > 0 ||
      exceededMaxEmails.length > 0
    ) {
      hasInvalidEmailCallback(true);
      callback(
        <div>
          {alreadyMembers.length > 0 && (
            <div style={{ color: 'orange', marginBottom: '4px' }}>
              Already {alreadyMembers.length > 1 ? 'members' : 'a member'} in this organisation
            </div>
          )}
          {invalidEmails.length > 0 && (
            <div style={{ color: 'red' }}>
              Invalid email {invalidEmails.length > 1 ? 'addresses' : 'address'}
            </div>
          )}
          {duplicatedEmails.length > 0 && (
            <div style={{ color: 'orange' }}>
              Duplicate email {duplicatedEmails.length > 1 ? 'addresses' : 'address'} detected
            </div>
          )}
          {exceededMaxEmails.length > 0 && (
            <div style={{ color: 'red' }}>
              Bulk invite limit is {maxInvitationLimit} users. Remove {exceededMaxEmails.length}{' '}
              {exceededMaxEmails.length > 1 ? ' users' : ' user'} to continue
            </div>
          )}
        </div>
      );
      return;
    }
    hasInvalidEmailCallback(false);
    callback();
  };

  return (
    <Form.Item
      label="Email addresses (comma separated):"
      style={{
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {getFieldDecorator('emailAddresses', {
        initialValue: '',
        validateTrigger: ['onBlur', 'onChange', 'onInput', 'onKeyUp', 'onPaste'],
        rules: [{ validator: emailValidator }]
      })(
        <div>
          <div style={{ marginBottom: '8px' }}>
            {emailTags.map(tag => (
              <Tag
                key={tag}
                closable
                onClose={() => handleTagClose(tag)}
                color={getTagColor(tag)}
                style={{ marginBottom: '1px' }}
                data-testid="email-tag"
              >
                {tag}
              </Tag>
            ))}
          </div>
          <Input.TextArea
            aria-label="Email addresses (comma separated):"
            rows={4}
            spellCheck={false}
            value={inputValue}
            onChange={handleInputChange}
            onPressEnter={keyEnterHandler}
            onBlur={blurHandler}
            placeholder="Type email(s) and press enter"
            disabled={disabled}
          />
        </div>
      )}
    </Form.Item>
  );
};

InvitedUsersEmailsField.propTypes = {
  form: PropTypes.any.isRequired,
  memberEmails: PropTypes.array.isRequired,
  onEmailTagsChange: PropTypes.func.isRequired,
  emailTags: PropTypes.array.isRequired,
  disabled: PropTypes.bool.isRequired,
  hasInvalidEmailCallback: PropTypes.func.isRequired
};

export default Form.create<InvitedUsersEmailsFieldProps>()(InvitedUsersEmailsField);
